import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllAccounts } from "../accounts/AccountService";
import { getInvestments } from "../investment/InvestmentService";
import "./NetWorthPage.css";

const ASSET_TYPES    = new Set(["BANK", "CASH", "WALLET", "UPI"]);
const LIABILITY_TYPES = new Set(["CREDIT_CARD", "LOAN_ACCOUNT"]);

function fmt(n) {
  if (n == null) return "₹0";
  const abs = Math.abs(Number(n));
  if (abs >= 1_00_00_000) return `₹${(abs / 1_00_00_000).toFixed(2)} Cr`;
  if (abs >= 1_00_000)    return `₹${(abs / 1_00_000).toFixed(2)} L`;
  if (abs >= 1_000)       return `₹${(abs / 1_000).toFixed(1)} K`;
  return `₹${Number(n).toFixed(2)}`;
}

export default function NetWorthPage() {
  const nav = useNavigate();
  const [accounts, setAccounts]       = useState([]);
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading]         = useState(true);

  useEffect(() => {
    Promise.all([getAllAccounts(), getInvestments()])
      .then(([accs, invs]) => {
        setAccounts(Array.isArray(accs) ? accs : []);
        setInvestments(Array.isArray(invs) ? invs : []);
      })
      .finally(() => setLoading(false));
  }, []);

  const assets     = accounts.filter(a => ASSET_TYPES.has(a.type));
  const liabilities = accounts.filter(a => LIABILITY_TYPES.has(a.type));

  const totalAssets      = assets.reduce((s, a) => s + Number(a.currentBalance || 0), 0);
  const totalLiabilities = liabilities.reduce((s, a) => s + Math.abs(Number(a.currentBalance || 0)), 0);
  const activeInvs       = investments.filter(i => i.investmentStatus === "ACTIVE" && !i.isRemoved);
  const totalInvested    = activeInvs.reduce((s, i) => s + Number(i.principal || 0), 0);
  const netWorth         = totalAssets + totalInvested - totalLiabilities;
  const isPositive       = netWorth >= 0;

  if (loading) return <div className="nw-loading">Loading net worth…</div>;

  return (
    <div className="nw-page">
      <div className="nw-header">
        <button className="nw-back-btn" onClick={() => nav(-1)}>← Back</button>
        <h2 className="nw-title">Net Worth</h2>
      </div>

      {/* Big number */}
      <div className="nw-hero">
        <div className="nw-hero-label">Your Net Worth</div>
        <div className={`nw-hero-value ${isPositive ? "nw-positive" : "nw-negative"}`}>
          {isPositive ? "" : "-"}{fmt(Math.abs(netWorth))}
        </div>
        <div className="nw-hero-sub">as of today</div>
      </div>

      {/* Summary bar */}
      <div className="nw-summary-bar">
        <SummaryCard label="Bank & Cash" value={totalAssets}      accent="green" icon="🏦" />
        <SummaryCard label="Investments" value={totalInvested}    accent="blue"  icon="📈" />
        <SummaryCard label="Liabilities" value={totalLiabilities} accent="red"   icon="💳" negative />
      </div>

      {/* Net worth formula */}
      <div className="nw-formula">
        <span className="nw-formula-chunk nw-formula-pos">{fmt(totalAssets)} (bank)</span>
        <span className="nw-formula-op">+</span>
        <span className="nw-formula-chunk nw-formula-pos">{fmt(totalInvested)} (investments)</span>
        <span className="nw-formula-op">−</span>
        <span className="nw-formula-chunk nw-formula-neg">{fmt(totalLiabilities)} (debt)</span>
        <span className="nw-formula-op">=</span>
        <span className={`nw-formula-chunk nw-formula-total ${isPositive ? "nw-formula-pos" : "nw-formula-neg"}`}>
          {fmt(netWorth)}
        </span>
      </div>

      <div className="nw-sections">
        {/* Assets by account */}
        <section className="nw-section">
          <h3 className="nw-section-title">Bank & Cash Accounts</h3>
          {assets.length === 0 ? (
            <p className="nw-empty">No bank/cash accounts found.</p>
          ) : (
            <AccountTable accounts={assets} />
          )}
          <div className="nw-section-total">
            Total <strong>{fmt(totalAssets)}</strong>
          </div>
        </section>

        {/* Investments */}
        <section className="nw-section">
          <h3 className="nw-section-title">Active Investments (FD / RD / MF)</h3>
          {activeInvs.length === 0 ? (
            <p className="nw-empty">No active investments found.</p>
          ) : (
            <table className="nw-table">
              <thead>
                <tr><th>Name</th><th>Type</th><th>Provider</th><th>Principal</th><th>Maturity</th></tr>
              </thead>
              <tbody>
                {activeInvs.map(inv => (
                  <tr key={inv.id}>
                    <td>{inv.investmentName || "—"}</td>
                    <td><span className="nw-type-chip">{inv.investmentType}</span></td>
                    <td>{inv.investmentProvider || "—"}</td>
                    <td className="nw-amount">{fmt(inv.principal)}</td>
                    <td>{inv.maturityDate || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="nw-section-total">
            Total <strong>{fmt(totalInvested)}</strong>
          </div>
        </section>

        {/* Liabilities */}
        <section className="nw-section">
          <h3 className="nw-section-title">Liabilities (Credit Cards & Loans)</h3>
          {liabilities.length === 0 ? (
            <p className="nw-empty">No credit card or loan accounts.</p>
          ) : (
            <AccountTable accounts={liabilities} negative />
          )}
          <div className="nw-section-total">
            Total <strong className="nw-negative-text">{fmt(totalLiabilities)}</strong>
          </div>
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, accent, icon, negative }) {
  return (
    <div className={`nw-summary-card nw-summary-${accent}`}>
      <div className="nw-summary-icon">{icon}</div>
      <div className="nw-summary-value">{negative ? "-" : ""}{fmt(value)}</div>
      <div className="nw-summary-label">{label}</div>
    </div>
  );
}

function AccountTable({ accounts, negative }) {
  return (
    <table className="nw-table">
      <thead>
        <tr><th>Account</th><th>Type</th><th>Number</th><th>Balance</th></tr>
      </thead>
      <tbody>
        {accounts.map(a => (
          <tr key={a.id}>
            <td>{a.accountName || a.id}</td>
            <td><span className="nw-type-chip">{a.type}</span></td>
            <td>{a.accountNumber ? `••${String(a.accountNumber).slice(-4)}` : "—"}</td>
            <td className={`nw-amount ${negative ? "nw-negative-text" : ""}`}>
              {negative ? "-" : ""}{fmt(Math.abs(a.currentBalance || 0))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
